import type { AxiosResponse } from "axios"

// A quota and its current usage, in bytes
export type QuotaUsage = {
  // Limit in bytes; null means no limit
  quota: number | null
  // Current usage in bytes (completed uploads and downloads only)
  usage: number
}

// Quotas and current usage of a team
export type TeamQuotaUsage = {
  // Total stored bytes
  storage: QuotaUsage
  // Downloaded bytes in the current UTC calendar month; resets every month
  egress: QuotaUsage
  // All downloaded bytes; never resets
  lifetime_egress: QuotaUsage
}

class UsageService {
    /**
     * @description Service base URL (without trailing `/`)
     */
    public static base_url: string = import.meta.env.VITE_APP_USAGE_SERVICE_URL

    public static axios: any

    public static init (axiosInstance: any) {
      UsageService.axios = axiosInstance
    }

    /**
     * @description Quotas and current usage of the logged in user's team
     */
    public static get_my_quota(): Promise<AxiosResponse<TeamQuotaUsage>> {
      return UsageService.axios.get(UsageService.base_url + '/quota')
    }

    /**
     * @description Quotas and current usage of a team. Only the caller's own team is allowed (403 otherwise)
     */
    public static get_team_quota(team_id: number): Promise<AxiosResponse<TeamQuotaUsage>> {
      return UsageService.axios.get(`${UsageService.base_url}/quota/${team_id}`)
    }

    public static get_downloads_series(namespace_id: Number, from: Date, to: Date, aggregation: "days" | "hours") {
      const params = {
        namespace_id: namespace_id,
        from: Math.floor(from.getTime() / 1000),
        to: Math.floor(to.getTime() / 1000),
        resolution: aggregation
      }
      return UsageService.axios.get(UsageService.base_url + '/series/downloads', {params: params})
    }

    public static get_storage_series(namespace_id: Number, from: Date, to: Date, aggregation: "days" | "hours") {
      const params = {
        namespace_id: namespace_id,
        from: Math.floor(from.getTime() / 1000),
        to: Math.floor(to.getTime() / 1000),
        resolution: aggregation
      }
      return UsageService.axios.get(UsageService.base_url + '/series/storage', {params: params})
    }

}

export default UsageService
